<template>
  <RevBreadcrumbs
    :ariaLabel="i18n(translations.breadcrumbs)"
    :breads
    class="px-16 md:px-24 lg:px-72"
  />
  <div
    v-if="journey"
    class="py-16 md:py-32 px-24 flex flex-col md:flex-row gap-16 md:gap-32 justify-center max-w-[928px] m-auto"
  >
    <div class="basis-1/2">
      <RevTag
        v-if="journey.deliveryTag !== 'UNTRACKED'"
        class="mb-12"
        :label="deliveryTagLabel"
        :variant="deliveryTagVariant"
      />
      <dl>
        <div v-if="shouldDisplayDeliveryDate" class="mb-12">
          <dt class="caption block">
            {{ i18n(translations.estimatedDelivery) }}
          </dt>
          <dd class="punchline-sans block">
            {{ mainDeliveryDate }}
          </dd>
          <dd v-if="previousDeliveryDate" class="line-through body-1">
            {{ previousDeliveryDate }}
          </dd>
        </div>

        <div class="mb-12">
          <dt class="caption block">
            {{ i18n(translations.deliveryBy) }}
          </dt>
          <dd class="body-1-bold block">{{ journey.carrierName }}</dd>
        </div>

        <div>
          <dt class="caption block">{{ i18n(translations.trackingId) }}</dt>
          <dd class="body-1-bold block">
            <RevLink
              class="inline-flex items-center gap-2 mb-4"
              rel="noopener noreferrer"
              target="_blank"
              :to="journey.trackingUrl"
              @click="trackOpenCarrierTracking"
            >
              {{ journey.trackingNumber }}
              <IconExternalLink size="24" />
            </RevLink>
          </dd>
        </div>
      </dl>

      <div class="my-12">
        <p v-if="isUpdated" class="body-2 mb-4">
          {{ i18n(translations.needHelp) }}
        </p>
        <p class="body-2">
          <FormattedMessage
            :definition="
              isUpdated
                ? translations.manageOrRescheduleDelivery
                : translations.forDeliveryQuestions
            "
          >
            <template #contactCarrierLink>
              <RevLink
                class="inline-flex items-center gap-2"
                rel="noopener noreferrer"
                target="_blank"
                :to="carrierContactUrl"
              >
                {{
                  i18n(translations.contactCarrier, {
                    carrierName: journey.carrierName,
                  })
                }}
                <IconExternalLink size="16" />
              </RevLink>
            </template>
          </FormattedMessage>
        </p>
        <p class="body-2">
          <FormattedMessage
            :definition="
              isUpdated
                ? translations.reportMissingOrDamaged
                : translations.packageMissingOrDamaged
            "
          >
            <template #contactBackMarketLink>
              <RevLink :to="backMarketContactUrl">
                {{ i18n(translations.contactBackMarket) }}
              </RevLink>
            </template>
          </FormattedMessage>
        </p>
      </div>
    </div>
    <div class="basis-1/2">
      <RevInfoBlock
        v-if="isDelayed"
        class="mb-12"
        :content="i18n(translations.delayedAlertDescription)"
        :icon="IconWarning"
        :title="
          i18n(translations.delayedAlertTitle, {
            carrierName: journey.carrierName,
          })
        "
        variant="warning"
      >
        <RevButton
          rel="noopener noreferrer"
          size="small"
          target="_blank"
          :to="journey.trackingUrl"
          variant="primary"
          @click="trackOpenCarrierTracking"
        >
          {{
            i18n(translations.delayedAlertButton, {
              carrierName: journey.carrierName,
            })
          }}
          <IconExternalLink class="inline" size="20" />
        </RevButton>
      </RevInfoBlock>

      <RevCard v-if="journey.steps?.length" class="px-16 py-1">
        <RevStatusTracker
          class="mt-24"
          :current-step="currentStepIndex"
          orientation="vertical"
          progress-bar-color="success"
          start-with="last"
          :steps="statusTrackerSteps"
        >
          <template
            v-for="step in statusTrackerSteps"
            :key="step.key"
            #[`${step.key}-content`]
          >
            <div class="flex text-action-default-low">
              <p class="caption grow">
                <span v-if="step.date" class="block">
                  {{ formatStepDateTime(step.date) }}
                </span>
                <template v-if="step.address">
                  <span
                    v-if="formatAddressRecipient(step.address)"
                    class="block"
                  >
                    {{ formatAddressRecipient(step.address) }}
                  </span>
                  <span class="block">
                    {{ formatAddressWithoutRecipient(step.address) }}
                  </span>
                </template>
              </p>
              <RevTooltip
                v-if="step.name === 'AVAILABLE_FOR_COLLECTION' && step.address"
                class="shrink-0 flex items-center"
                position="top"
              >
                <template #trigger="{ show, hide }">
                  <RevButtonIcon
                    :aria-label="i18n(translations.copyAddress)"
                    :icon="IconCopy"
                    size="small"
                    variant="ghost"
                    @click="handleCopyAddress(step.address, show, hide)"
                    @focusout="hide"
                    @mouseleave="hide"
                  />
                </template>
                {{ i18n(translations.addressCopied) }}
              </RevTooltip>
            </div>
          </template>
        </RevStatusTracker>
      </RevCard>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAsyncData, useRoute, useRouteLocationWithLocale } from '#imports'
import { computed } from 'vue'

import { useFormatAddress } from '@backmarket/nuxt-module-address/useFormatAddress'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevBreadcrumbs } from '@ds/components/Breadcrumbs'
import type { Bread } from '@ds/components/Breadcrumbs/Breadcrumbs.vue'
import { RevButton } from '@ds/components/Button'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevCard } from '@ds/components/Card'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevLink } from '@ds/components/Link'
import { RevStatusTracker } from '@ds/components/StatusTracker'
import { RevTag } from '@ds/components/Tag'
import { RevTooltip } from '@ds/components/Tooltip'
import { IconCopy } from '@ds/icons/IconCopy'
import { IconExternalLink } from '@ds/icons/IconExternalLink'
import { IconWarning } from '@ds/icons/IconWarning'

import type { Journey } from '~/api/tracking/types'
import { useOrderlineDetails } from '~/scopes/care-commons/components/order/OrderlineCard/composables/useOrderlineDetails'
import { CUSTOMER_CARE } from '~/scopes/customer-care/route-names'
import { ROUTE_NAMES } from '~/scopes/dashboard/constants'

import {
  CARRIER_TRACKING_URL_TRACK_CLICK_NAME,
  CARRIER_TRACKING_URL_TRACK_CLICK_ZONE,
  PARCEL_JOURNEY_TYPE_FORWARD,
  TRACK_PARCEL_TRACK_CLICK_TARGET_INTERNAL,
} from './TrackParcel.constants'
import translations from './TrackParcel.translations'

const route = useRoute()
const $httpFetch = useHttpFetch()
const locale = useI18nLocale()
const i18n = useI18n()
const resolveLocalizedRoute = useRouteLocationWithLocale()
const { trackClick } = useTracking()

const orderlinePublicId = computed(
  () => route.params.orderlinePublicId as string,
)
const { fetchOrderlineDetails } = useOrderlineDetails()
const orderlineDetailsKey = getAsyncDataKey('orderlineDetails', {
  orderlineId: orderlinePublicId.value,
})

const { data: orderlineDetails } = await useAsyncData(orderlineDetailsKey, () =>
  fetchOrderlineDetails(orderlinePublicId.value),
)

const orderlineId = computed(() => orderlineDetails.value?.orderlineId)

const breads: Array<Bread> = [
  {
    title: i18n(translations.breadcrumbOrders),
    link: resolveLocalizedRoute({ name: ROUTE_NAMES.DASHBOARD.MY_ORDERS.SELF }),
  },
  {
    title: i18n(translations.breadcrumbTrackShipment),
    link: resolveLocalizedRoute({
      name: ROUTE_NAMES.DASHBOARD.MY_ORDERS.TRACK_PARCEL,
    }),
  },
]

const { data: journeys } = await useAsyncData<Journey[]>(
  `tracking-journey-${orderlinePublicId.value}`,
  () =>
    $httpFetch('/tracking/api/v1/parcels/journeys', {
      query: {
        entityId: orderlinePublicId.value,
        entityType: 'SALES_ORDERLINE',
      },
    }),
)

// Get the first journey (For now the endpoint returns only 1 element)
const journey = computed(() => journeys.value?.[0])

const deliveryTagVariant = computed(() => {
  if (!journey.value) return 'success'

  switch (journey.value.deliveryTag) {
    case 'ON_TRACK':
    case 'DELIVERED':
      return 'success'
    case 'UPDATED':
      return 'warning'
    case 'DELAYED':
      return 'danger'
    default:
      return 'success'
  }
})

const deliveryTagLabel = computed(() => {
  if (!journey.value) return ''

  switch (journey.value.deliveryTag) {
    case 'ON_TRACK':
      return i18n(translations.deliveryOnTrack)
    case 'UPDATED':
      return i18n(translations.updatedDeliveryDate)
    case 'DELAYED':
      return i18n(translations.deliveryDelayed)
    case 'DELIVERED':
      return i18n(translations.delivered)
    default:
      return ''
  }
})

const isToday = (date: Date) => {
  const today = new Date()

  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  )
}

const formatSingleDate = (date: Date) => {
  const weekday = isToday(date)
    ? i18n(translations.today)
    : i18n.date(date, { weekday: 'long' })
  const formattedDate = i18n.date(date, {
    month: 'long',
    day: 'numeric',
  })

  return `${weekday}, ${formattedDate}`
}

const formatDateRange = (minDate: Date, maxDate: Date) => {
  const isSameMonth =
    minDate.getMonth() === maxDate.getMonth() &&
    minDate.getFullYear() === maxDate.getFullYear()

  if (isSameMonth) {
    const month = i18n.date(minDate, { month: 'long' })

    return `${month} ${minDate.getDate()} - ${maxDate.getDate()}`
  }

  const minFormattedDate = i18n.date(minDate, {
    month: 'long',
    day: 'numeric',
  })
  const maxFormattedDate = i18n.date(maxDate, {
    month: 'long',
    day: 'numeric',
  })

  return `${minFormattedDate} - ${maxFormattedDate}`
}

const shouldDisplayDeliveryDate = computed(() => {
  if (!journey.value) return false

  return journey.value.deliveryTag !== 'DELAYED'
})

const promisedDeliveryDateRange = computed(() => {
  if (!journey.value) return ''

  const minDate = new Date(journey.value.promisedMinDeliveryDate)
  const maxDate = new Date(journey.value.promisedMaxDeliveryDate)

  return formatDateRange(minDate, maxDate)
})

const mainDeliveryDate = computed(() => {
  if (!journey.value) return ''

  if (journey.value.estimatedDeliveryDate) {
    const date = new Date(journey.value.estimatedDeliveryDate)

    return formatSingleDate(date)
  }

  return promisedDeliveryDateRange.value
})

const previousDeliveryDate = computed(() => {
  if (!journey.value?.estimatedDeliveryDate) return ''

  if (journey.value.previousEstimatedDeliveryDate) {
    const date = new Date(journey.value.previousEstimatedDeliveryDate)

    return formatSingleDate(date)
  }

  return promisedDeliveryDateRange
})

const carrierContactUrl = computed(() => {
  return `https://help.backmarket.com/hc/${locale}/categories/360002120993`
})

const backMarketContactUrl = computed(() => {
  if (!orderlineId.value) return ''

  return resolveLocalizedRoute({
    name: CUSTOMER_CARE.DIAGNOSIS.SELF,
    params: {
      orderlineId: orderlineId.value,
    },
  })
})

const isDelayed = computed(() => journey.value?.deliveryTag === 'DELAYED')
const isUpdated = computed(() => journey.value?.deliveryTag === 'UPDATED')

function trackOpenCarrierTracking() {
  trackClick({
    name: CARRIER_TRACKING_URL_TRACK_CLICK_NAME,
    zone: CARRIER_TRACKING_URL_TRACK_CLICK_ZONE,
    values: {
      journey: PARCEL_JOURNEY_TYPE_FORWARD,
      orderlinePublicId: orderlinePublicId.value,
      target: TRACK_PARCEL_TRACK_CLICK_TARGET_INTERNAL,
    },
  })
}

const statusTrackerSteps = computed(() => {
  if (!journey.value?.steps) return []

  return journey.value.steps.map((step, index) => ({
    key: `step-${index}`,
    label: step.name,
    name: step.name,
    date: step.date,
    address: step.address,
  }))
})

const currentStepIndex = computed(() => {
  if (!journey.value?.steps) return 'step-0'

  const lastCompletedIndex = journey.value.steps.findLastIndex(
    (step) => step.completed,
  )

  // IRL, API would always return "order placed" step with completed = true at least
  // So lastCompletedIndex is >= 0
  return `step-${lastCompletedIndex}`
})

const formatStepDateTime = (date: string) => {
  const dateObj = new Date(date)
  const formattedDate = i18n.date(dateObj, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
  const formattedTime = i18n.date(dateObj, {
    hour: '2-digit',
    minute: '2-digit',
  })

  return `${formattedDate}, ${formattedTime}`
}

const formatAddressRecipient = (
  address: NonNullable<typeof journey.value>['steps'][number]['address'],
) => {
  if (!address) return ''

  const name = [address.firstName, address.lastName].filter(Boolean).join(' ')

  return name || address.company || ''
}

const formatAddressWithoutRecipient = (
  address: NonNullable<typeof journey.value>['steps'][number]['address'],
) => {
  if (!address) return ''

  const lines = useFormatAddress(address, {
    includeName: false,
    includeCompany: false,
  })

  return lines.join(', ')
}

const formatAddressInline = (
  address: NonNullable<typeof journey.value>['steps'][number]['address'],
) => {
  if (!address) return ''

  const recipient = formatAddressRecipient(address)
  const addressWithoutRecipient = formatAddressWithoutRecipient(address)

  return recipient
    ? `${recipient}, ${addressWithoutRecipient}`
    : addressWithoutRecipient
}

const handleCopyAddress = async (
  address: NonNullable<typeof journey.value>['steps'][number]['address'],
  show: () => void,
  hide: () => void,
) => {
  try {
    await navigator.clipboard.writeText(formatAddressInline(address))
    show()

    setTimeout(() => {
      hide()
    }, 1000)
  } catch {
    // Do nothing
  }
}
</script>
